/**
 * MLB postseason games as search sees them (functions/api/search.ts): which round, game and clubs
 * a listing is, whichever partner named it. shared/postseason.mjs reads SeatGeek's and
 * Ticketmaster's names and our own labels ("TBD at Brewers - World Series (Gm 1, HG 1)", "World
 * Series Game 3: Brewers at Guardians"); TicketNetwork names its games the other way round,
 * "American League Championship Series: Tampa Bay Rays vs. Cleveland Guardians - Home Game 3, Series
 * Game 6", and files some under Theater, so neither the event's kind nor its category says it is a
 * ballgame — the round in the name and an MLB club on a side do. The College World Series, the
 * Little League's and the World Series of Poker name no MLB club: they are no MLB game.
 *
 * WHY (2026-10-19, search-gaps.md): "world series" answered with the MLB league chip and SeatGeek's
 * games only, "alcs" said no ALCS game was listed while TicketNetwork listed Game 6 that night, and
 * "rays" led with the Rays' Spring Training (TicketNetwork's row sits on its performer's page, not
 * the club's).
 *
 * Pure: no database, no clock. Kept apart from shared/postseason.mjs, which the build's World
 * Series data work owns.
 */
import { exchangeSaysPlayed, mlbNickname, parsePostseason, postseasonRound } from './postseason.mjs';

/** @typedef {import('./postseason.mjs').Round} Round */
/** @typedef {{ round: Round, game: number | null, homeGame: number | null, clubs: string[], ifNecessary: boolean }} SearchGame */

/** TicketNetwork: "Round: Home vs. Away - Home Game 3, Series Game 6" (the details are optional). */
const TN = /^([^:]{3,80}):\s*(.+?)\s+(?:vs\.?|v\.?|at)\s+(.+?)(?:\s+-\s+(.+))?$/i;
/** The part before the colon is an MLB round and nothing else (a year or "MLB" aside). */
const ROUND_ONLY =
  /^(?:world series|alcs|nlcs|alds|nlds|(?:american|national) league (?:championship|division|wild card) series|(?:al|nl) (?:championship series|division series|wild card(?: series)?))$/i;
/** "TBD", "To Be Determined": no club yet. @param {string} s */
const open = (s) => /^(?:tb[ad]|to be (?:determined|announced)|opponent tb[ad])$/i.test(s.trim());

/**
 * An MLB postseason game from a listing, or null for anything else (another sport's playoffs, the
 * College World Series, a name we cannot read with certainty).
 * `clubs`: the MLB clubs it names, by nickname ("Rays"), home first; a side still to be decided is
 * left out. `game`: its number in the series, when the name says it.
 * @param {{ name?: string | null, artist?: string | null, id?: string | null, sure?: boolean }} row
 * @returns {SearchGame | null}
 */
export const postseasonGame = (row) => {
  const name = String(row?.name ?? '').replace(/\s+/g, ' ').trim();
  const p = parsePostseason(name, row);
  if (p) {
    if (!mlbNickname(p.home)) return null;
    const clubs = [p.home, p.away].map((x) => mlbNickname(x)).filter((x) => x !== null);
    // The exchange marks every game that may not be played and drops the mark once a game is
    // certain (shared/postseason.mjs exchangeSaysPlayed); HelloTickets copies its form without the
    // mark, so an ht- row keeps the number rule, as postseasonDoubts does for pages.
    const sure = row?.sure === true || (exchangeSaysPlayed(name) && !String(row?.id ?? '').startsWith('ht-'));
    return { round: p.round, game: p.game, homeGame: p.homeGame, clubs: /** @type {string[]} */ (clubs), ifNecessary: p.ifNecessary && !sure };
  }
  const m = name.match(TN);
  if (!m) return null;
  const head = m[1].replace(/\b(?:19|20)\d\d\b/g, ' ').replace(/\bmlb\b/gi, ' ').replace(/\s+/g, ' ').trim();
  const round = /** @type {Round | null} */ (ROUND_ONLY.test(head) ? postseasonRound(head) : null);
  if (!round) return null;
  const sides = [m[2], m[3]].map((s) => s.replace(/\s*\([^)]*\)\s*$/, '').trim());
  const named = sides.filter((s) => !open(s));
  const clubs = named.map((s) => mlbNickname(s));
  if (!named.length || clubs.some((c) => !c)) return null;
  const tail = m[4] ?? '';
  const game = tail.match(/\bseries\s+game\s*#?\s*(\d{1,2})\b/i);
  const home = tail.match(/\bhome\s+game\s*#?\s*(\d{1,2})\b/i);
  return {
    round,
    game: game ? Number(game[1]) : null,
    homeGame: home ? Number(home[1]) : null,
    clubs: /** @type {string[]} */ (clubs),
    ifNecessary: /\bif\s+necessary\b/i.test(name),
  };
};

/**
 * The MLB club a list page is, by its nickname ("Tampa Bay Rays" → "Rays"), or null — only a team
 * page the build files under MLB (its `league`): the New York Rangers and the Arizona Cardinals
 * share a nickname with an MLB club.
 * @param {{ kind?: string | null, name?: string | null, league?: string | null } | null | undefined} page
 */
export const mlbClubOf = (page) => (page?.kind === 'team' && page.league === 'mlb' ? mlbNickname(page.name) : null);

/**
 * The MLB nicknames among team page keys a search looks up ("team:tampa-bay-rays" → Rays,
 * "team:red-sox" → Red Sox), at most four: which clubs' postseason games to read alongside. A
 * hockey or football club with a baseball nickname is in too (its page is no MLB page: mlbClubOf
 * keeps its answer as it was).
 * @param {Iterable<string>} keys page keys ("team:…", others ignored)
 * @returns {string[]}
 */
export const mlbNicknamesIn = (keys) => {
  const out = new Set();
  for (const k of keys) {
    if (!k.startsWith('team:')) continue;
    const name = k.slice(5).split('-').map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(' ');
    const n = mlbNickname(name);
    if (n) out.add(n);
    if (out.size >= 4) break;
  }
  return [...out];
};

/** The words an MLB postseason listing carries, whichever partner wrote it. */
const ROUND_PHRASES = ['world series', 'championship series', 'division series', 'wild card', 'alcs', 'nlcs', 'alds', 'nlds'];

/**
 * A full-text expression for these clubs' postseason listings: a nickname and a round's words
 * ("rays" AND "world series" …). shared/postseason-search.mjs postseasonGame then keeps the games.
 * @param {string[]} nicknames
 */
export const postseasonMatch = (nicknames) =>
  `(${nicknames.map((n) => `"${n.toLowerCase().replace(/"/g, '')}"`).join(' OR ')}) AND (${ROUND_PHRASES.map((p) => `"${p}"`).join(' OR ')})`;

/**
 * A club's postseason games among rows the search already has (its page's own dates, the rows its
 * words matched, and its postseason listings read with them: postseasonMatch): the MLB postseason
 * games that name it, from `today` to `until`, date order, each once. Empty outside the postseason,
 * so the club's answer is then what it always was.
 * @template {{ id: string, name: string, artist?: string | null, event_date: string }} R
 * @param {R[]} rows
 * @param {string} club nickname ("Rays")
 * @param {string} today YYYY-MM-DD
 * @param {string} until YYYY-MM-DD, the last day that counts
 * @returns {R[]}
 */
export const clubPostseason = (rows, club, today, until) => {
  const seen = new Set();
  return rows
    .filter((r) => {
      if (seen.has(r.id) || r.event_date < today || r.event_date > until) return false;
      seen.add(r.id);
      return postseasonGame(r)?.clubs.includes(club) ?? false;
    })
    .sort((a, b) => a.event_date.localeCompare(b.event_date));
};
